"use client";
import Link from "next/link";
import { motion, useMotionValue, useTransform, useSpring } from "framer-motion";
import Button from "@/components/ui/button";
import Navbar from "@/components/navbar";
import { useTranslations } from "next-intl";

export default function Hero() {
  const t = useTranslations("home");
  const STATS = t.raw("heroStats");
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const springConfig = { stiffness: 100, damping: 30 };
  const cardX = useSpring(useTransform(mouseX, [-400, 400], [-10, 10]), springConfig);
  const cardY = useSpring(useTransform(mouseY, [-400, 400], [-10, 10]), springConfig);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    mouseX.set(e.clientX - rect.left - rect.width / 2);
    mouseY.set(e.clientY - rect.top - rect.height / 2);
  };

  return (
    <section
      className="relative min-h-screen bg-zinc-950 flex flex-col overflow-hidden"
      onMouseMove={handleMouseMove}
    >
      <Navbar />

      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[70%] h-[70%] bg-[radial-gradient(ellipse_80%_60%_at_70%_0%,rgba(124,58,237,0.18),transparent_70%)]" />
        <div className="absolute bottom-0 left-0 w-[40%] h-[40%] bg-[radial-gradient(ellipse,rgba(6,182,212,0.06),transparent_70%)]" />
        <div className="absolute inset-0 [background-image:radial-gradient(circle,rgba(255,255,255,0.05)_1px,transparent_1px)] [background-size:24px_24px]" />
      </div>

      <div className="relative flex-1 flex items-center max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center w-full">

          {/* Left: Content */}
          <div className="flex flex-col gap-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-zinc-400 w-fit"
            >
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              {t("heroBadge")}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
              className="text-[clamp(48px,7vw,84px)] font-bold leading-[1.05] tracking-tight"
            >
              <span className="text-white">{t("tagline")}</span>
              <br />
              <span className="bg-gradient-to-r from-violet-400 to-cyan-400 bg-clip-text text-transparent">
                {t("tagline2")}
              </span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="text-lg text-zinc-400 max-w-lg leading-relaxed"
            >
              {t("heroDescription")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="flex flex-wrap gap-3"
            >
              <Link href="/contact">
                <Button variant="primary" size="lg">
                  {t("buttonTitle")}
                </Button>
              </Link>
              <Link href="/portfolio">
                <Button variant="secondary" size="lg">
                  {t("heroViewWork")}
                </Button>
              </Link>
            </motion.div>
          </div>

          {/* Right: Floating stats card */}
          <motion.div
            style={{ x: cardX, y: cardY }}
            className="hidden lg:block"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 0.7 }}
              className="relative rounded-2xl bg-white border border-zinc-200 shadow-xl shadow-violet-200/60 dark:bg-white/[0.03] dark:border-white/10 dark:shadow-2xl dark:shadow-violet-500/10 backdrop-blur-xl p-6"
            >
              {/* gradient overlay — subtle tint */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-violet-500/5 to-cyan-500/5 dark:from-violet-500/10 dark:to-cyan-500/5 pointer-events-none" />

              {/* violet glow ring */}
              <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-violet-400/20 via-transparent to-cyan-400/20 dark:from-violet-500/20 dark:to-cyan-500/10 pointer-events-none" />

              <div className="relative space-y-5">
                <div className="flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-500 font-mono">
                  <span className="w-2 h-2 rounded-full bg-green-500 shadow-sm shadow-green-400/60" />
                  {t("heroLiveLabel")}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {STATS.map(({ label, value }) => (
                    <div
                      key={label}
                      className="p-4 rounded-xl bg-zinc-50 border border-zinc-200 hover:border-violet-300 hover:shadow-sm hover:shadow-violet-200/50 dark:bg-white/[0.04] dark:border-white/10 dark:hover:border-violet-500/30 transition-all duration-200"
                    >
                      <div className="text-2xl font-mono font-bold bg-gradient-to-r from-violet-600 to-cyan-500 dark:from-violet-400 dark:to-cyan-400 bg-clip-text text-transparent">
                        {value}
                      </div>
                      <div className="text-xs text-zinc-500 mt-1 leading-tight">{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>

        </div>
      </div>

      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ delay: 1.2, duration: 1.5, ease: "easeInOut" }}
        className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-violet-500/50 to-transparent origin-left"
      />
    </section>
  );
}
